/**
 * Peke Survey - Reflection component for Peke's Adventures
 * Collects emotional feedback, favorite parts, and future story suggestions
 * Ages 3-5 - Visual + text input
 */

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { savePekeSurveyResponse } from '../../../lib/pekeSurveyOperations';

interface PekeSurveyProps {
  language: 'en' | 'es';
  onComplete: () => void;
  gameDurationSeconds?: number;
}

const TEXT = {
  en: {
    title: 'How did you feel?',
    question1: 'How do you feel now?',
    question2: 'What was your favorite part?',
    question3: 'What character or story would you like to tell next?',
    question3Placeholder: 'Tell us about your dream character or story...',
    skipText: 'Skip this question',
    continueText: 'Continue',
    thankYou: 'Thank you for sharing!',
    playAgain: 'Play Again',
    goHome: 'Home',
    continueButton: 'Continue',
    options1: [
      { emoji: '😊', label: 'Happy' },
      { emoji: '😍', label: 'Excited' },
      { emoji: '🤔', label: 'Thoughtful' },
      { emoji: '😌', label: 'Calm' },
      { emoji: '🤩', label: 'Amazed' }
    ],
    options2: [
      { emoji: '🎙️', label: 'Voice Narrative' },
      { emoji: '🖼️', label: "Peke's Illustrations" },
      { emoji: '📖', label: 'Your Story' },
      { emoji: '✨', label: 'Everything!' }
    ]
  },
  es: {
    title: '¿Cómo te sentiste?',
    question1: '¿Cómo te sientes ahora?',
    question2: '¿Cuál fue tu parte favorita?',
    question3: '¿Qué personaje o historia te gustaría contar después?',
    question3Placeholder: 'Cuéntanos sobre tu personaje o historia soñada...',
    skipText: 'Saltar esta pregunta',
    continueText: 'Continuar',
    thankYou: '¡Gracias por compartir!',
    playAgain: 'Jugar de Nuevo',
    goHome: 'Inicio',
    continueButton: 'Continuar',
    options1: [
      { emoji: '😊', label: 'Feliz' },
      { emoji: '😍', label: 'Emocionado' },
      { emoji: '🤔', label: 'Pensativo' },
      { emoji: '😌', label: 'Tranquilo' },
      { emoji: '🤩', label: 'Asombrado' }
    ],
    options2: [
      { emoji: '🎙️', label: 'Narrativa de Voz' },
      { emoji: '🖼️', label: 'Ilustraciones de Peke' },
      { emoji: '📖', label: 'Tu Historia' },
      { emoji: '✨', label: '¡Todo!' }
    ]
  }
};

// Shared design tokens with the rest of Peke / the games family.
const INK_GREEN = '#2a6d5b';
const BRAND_GREEN = '#42a57a';
const SERIF = "'Fraunces', Georgia, serif";
const SANS = "'Inter', system-ui, -apple-system, sans-serif";
const FEELING_PASTELS = ['#dbeef5', '#fce8d2', '#dcecda', '#fadcdc', '#e8defc'];
const FAVORITE_PASTELS = ['#dcecda', '#dbeef5', '#fce8d2', '#e8defc'];

const primaryPill: React.CSSProperties = {
  background: BRAND_GREEN,
  color: '#ffffff',
  fontFamily: SANS,
  fontSize: 20,
  fontWeight: 600,
  padding: '14px 32px',
  borderRadius: 999,
  border: 'none',
  cursor: 'pointer',
  boxShadow: '0 6px 22px rgba(66,165,122,0.32)',
};
const outlinePill: React.CSSProperties = {
  background: 'transparent',
  color: BRAND_GREEN,
  border: `1.5px solid ${BRAND_GREEN}`,
  fontFamily: SANS,
  fontSize: 18,
  fontWeight: 600,
  padding: '14px 28px',
  borderRadius: 999,
  cursor: 'pointer',
};

export default function PekeSurvey({ language, onComplete, gameDurationSeconds }: PekeSurveyProps) {
  const [step, setStep] = useState<1 | 2 | 3 | 'complete'>(1);
  const [selectedFeeling, setSelectedFeeling] = useState<string | null>(null);
  const [selectedFeelingLabel, setSelectedFeelingLabel] = useState<string | null>(null);
  const [selectedFavorite, setSelectedFavorite] = useState<string | null>(null);
  const [selectedFavoriteLabel, setSelectedFavoriteLabel] = useState<string | null>(null);
  const [futureStory, setFutureStory] = useState<string>('');
  const [isSaving, setIsSaving] = useState(false);

  const t = TEXT[language];

  const handleFeelingSelect = (emoji: string, label: string) => {
    setSelectedFeeling(emoji);
    setSelectedFeelingLabel(label);
    setTimeout(() => setStep(2), 800);
  };

  const handleFavoriteSelect = (emoji: string, label: string) => {
    setSelectedFavorite(emoji);
    setSelectedFavoriteLabel(label);
    setTimeout(() => setStep(3), 800);
  };

  const handleSubmit = async () => {
    if (!selectedFeeling || !selectedFavorite || !selectedFeelingLabel || !selectedFavoriteLabel) {
      return;
    }

    setIsSaving(true);

    // Save to database
    const result = await savePekeSurveyResponse({
      feelingEmoji: selectedFeeling,
      feelingLabel: selectedFeelingLabel,
      favoritePartEmoji: selectedFavorite,
      favoritePartLabel: selectedFavoriteLabel,
      futureStoryIdea: futureStory || null,
      language,
      gameDurationSeconds
    });

    setIsSaving(false);

    if (result.success) {
      console.log('Peke survey saved successfully');
    } else {
      console.error('Failed to save Peke survey:', result.error);
    }

    setStep('complete');
  };

  const handleSkipQuestion = () => {
    handleSubmit();
  };

  // Shared step header — Fraunces light, ink-green, with a small uppercase
  // "step n of 3" indicator above so the survey reads as a guided sequence.
  const StepHeader = ({ stepIndex, heading }: { stepIndex: 1 | 2 | 3; heading: string }) => (
    <>
      <p
        className="text-center"
        style={{
          fontFamily: SANS,
          fontSize: 13,
          fontWeight: 600,
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
          color: '#9a9a9a',
          marginBottom: 10,
        }}
      >
        {language === 'es' ? `Paso ${stepIndex} de 3` : `Step ${stepIndex} of 3`}
      </p>
      <h2
        className="text-center"
        style={{
          fontFamily: SERIF,
          fontWeight: 300,
          fontSize: 'clamp(28px, 4.5vw, 56px)',
          color: INK_GREEN,
          letterSpacing: '-0.015em',
          marginBottom: 32,
        }}
      >
        {heading}
      </h2>
    </>
  );

  // Shared emoji-tile — white outer + pastel inner + italic-Fraunces label.
  const EmojiTile = ({
    option,
    index,
    pastel,
    active,
    onSelect,
  }: {
    option: { emoji: string; label: string };
    index: number;
    pastel: string;
    active: boolean;
    onSelect: () => void;
  }) => (
    <motion.button
      initial={{ scale: 0.94, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ delay: index * 0.05, type: 'spring', stiffness: 220, damping: 22 }}
      whileHover={{ y: -3 }}
      whileTap={{ scale: 0.97 }}
      onClick={onSelect}
      style={{
        padding: 14,
        background: '#ffffff',
        border: active ? `2px solid ${BRAND_GREEN}` : '1px solid #e8e0c8',
        borderRadius: 18,
        cursor: 'pointer',
        transition: 'border-color 200ms ease',
      }}
    >
      <div
        style={{
          background: pastel,
          borderRadius: 14,
          padding: '24px 12px 18px',
          marginBottom: 12,
        }}
      >
        <div style={{ fontSize: 'clamp(56px, 9vw, 84px)', lineHeight: 1 }}>{option.emoji}</div>
      </div>
      <p
        style={{
          fontFamily: SERIF,
          fontStyle: 'italic',
          fontSize: 'clamp(16px, 2.2vw, 22px)',
          fontWeight: 600,
          color: INK_GREEN,
        }}
      >
        {option.label}
      </p>
    </motion.button>
  );

  return (
    <div className="min-h-[calc(100vh-80px)] flex flex-col items-center justify-center p-4 md:p-6">
      <div className="max-w-4xl w-full">
        <AnimatePresence mode="wait">
          {/* STEP 1 — How do you feel? */}
          {step === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -30 }}
            >
              <StepHeader stepIndex={1} heading={t.question1} />
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 md:gap-4">
                {t.options1.map((option, index) => (
                  <EmojiTile
                    key={option.emoji}
                    option={option}
                    index={index}
                    pastel={FEELING_PASTELS[index % FEELING_PASTELS.length]}
                    active={selectedFeeling === option.emoji}
                    onSelect={() => handleFeelingSelect(option.emoji, option.label)}
                  />
                ))}
              </div>
            </motion.div>
          )}

          {/* STEP 2 — Favorite part */}
          {step === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -30 }}
            >
              <StepHeader stepIndex={2} heading={t.question2} />
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                {t.options2.map((option, index) => (
                  <EmojiTile
                    key={option.emoji}
                    option={option}
                    index={index}
                    pastel={FAVORITE_PASTELS[index % FAVORITE_PASTELS.length]}
                    active={selectedFavorite === option.emoji}
                    onSelect={() => handleFavoriteSelect(option.emoji, option.label)}
                  />
                ))}
              </div>
            </motion.div>
          )}

          {/* STEP 3 — Future story idea (optional textarea) */}
          {step === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -30 }}
            >
              <StepHeader stepIndex={3} heading={t.question3} />

              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 }}
                className="max-w-2xl mx-auto p-5 md:p-7"
                style={{
                  background: '#ffffff',
                  border: '1px solid #e8e0c8',
                  borderRadius: 24,
                }}
              >
                <textarea
                  value={futureStory}
                  onChange={(e) => setFutureStory(e.target.value)}
                  placeholder={t.question3Placeholder}
                  maxLength={500}
                  className="w-full resize-none focus:outline-none"
                  style={{
                    height: 180,
                    padding: 18,
                    fontFamily: SERIF,
                    fontStyle: 'italic',
                    fontSize: 'clamp(18px, 2.4vw, 24px)',
                    fontWeight: 500,
                    color: INK_GREEN,
                    background: '#fbfbe8',
                    border: '1.5px solid rgba(42,109,91,0.25)',
                    borderRadius: 16,
                    outline: 'none',
                    transition: 'border-color 160ms ease',
                  }}
                  onFocus={(e) => { e.currentTarget.style.borderColor = BRAND_GREEN; }}
                  onBlur={(e) => { e.currentTarget.style.borderColor = 'rgba(42,109,91,0.25)'; }}
                />

                <p
                  className="text-right mt-2"
                  style={{
                    fontFamily: SANS,
                    fontSize: 12,
                    fontWeight: 600,
                    letterSpacing: '0.12em',
                    color: '#9a9a9a',
                  }}
                >
                  {futureStory.length} / 500
                </p>

                <div className="flex gap-3 justify-center mt-6 flex-wrap">
                  <motion.button
                    onClick={handleSkipQuestion}
                    style={{ ...outlinePill, opacity: isSaving ? 0.5 : 1, cursor: isSaving ? 'not-allowed' : 'pointer' }}
                    whileHover={{ scale: isSaving ? 1 : 1.04 }}
                    whileTap={{ scale: 0.97 }}
                    disabled={isSaving}
                  >
                    {t.skipText}
                  </motion.button>

                  <motion.button
                    onClick={handleSubmit}
                    style={{ ...primaryPill, opacity: isSaving ? 0.6 : 1, cursor: isSaving ? 'not-allowed' : 'pointer' }}
                    whileHover={{ scale: isSaving ? 1 : 1.04 }}
                    whileTap={{ scale: 0.97 }}
                    disabled={isSaving}
                  >
                    {isSaving ? '…' : `${t.continueText} →`}
                  </motion.button>
                </div>
              </motion.div>
            </motion.div>
          )}

          {/* COMPLETE — calm thank-you summary, no rainbow burst */}
          {step === 'complete' && (
            <motion.div
              key="complete"
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ type: 'spring', stiffness: 220, damping: 22 }}
              className="text-center mx-auto p-6 sm:p-10 md:p-14 max-w-2xl"
              style={{
                background: '#ffffff',
                border: '1px solid #e8e0c8',
                borderRadius: 24,
              }}
            >
              <motion.h2
                initial={{ y: 12, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.15 }}
                style={{
                  fontFamily: SERIF,
                  fontWeight: 300,
                  fontSize: 'clamp(32px, 6vw, 60px)',
                  color: INK_GREEN,
                  letterSpacing: '-0.015em',
                  marginBottom: 16,
                }}
              >
                {t.thankYou}
              </motion.h2>

              <motion.p
                initial={{ y: 12, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
                style={{
                  fontFamily: SERIF,
                  fontStyle: 'italic',
                  fontSize: 'clamp(20px, 2.6vw, 28px)',
                  fontWeight: 500,
                  color: INK_GREEN,
                  marginBottom: 28,
                }}
              >
                {selectedFeeling && <span style={{ fontSize: 'clamp(48px, 8vw, 72px)', marginRight: 16 }}>{selectedFeeling}</span>}
                {selectedFavorite && <span style={{ fontSize: 'clamp(48px, 8vw, 72px)' }}>{selectedFavorite}</span>}
              </motion.p>

              <motion.div
                initial={{ y: 12, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.45 }}
                className="flex justify-center"
              >
                <motion.button
                  onClick={onComplete}
                  style={primaryPill}
                  whileHover={{ scale: 1.04 }}
                  whileTap={{ scale: 0.97 }}
                >
                  {t.continueButton} →
                </motion.button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
